import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { ThemeContext } from "../contexts/ThemeContext";
import { useTenantApp } from "../hooks";
import { AVAILABLE_THEMES } from "@inloco/shared";

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
    const { app } = useTenantApp();

    const lightThemeConfig = app?.branding?.theming?.lightMode || 'light';
    const darkThemeConfig = app?.branding?.theming?.darkMode || 'dark';
    const isSingleTheme = app ? lightThemeConfig === darkThemeConfig : false;

    const [userTheme, setUserTheme] = useState<string | null>(() => {
        if (typeof window === 'undefined') return null;
        return localStorage.getItem('theme');
    });

    const activeTheme = useMemo(() => {

        if (!app) {
            return userTheme || 'light';
        }

        if (!userTheme) return lightThemeConfig;

        const isObsolete = userTheme !== lightThemeConfig && userTheme !== darkThemeConfig;

        if (isSingleTheme || isObsolete) {
            return lightThemeConfig;
        }

        return userTheme;
    }, [app, userTheme, lightThemeConfig, darkThemeConfig, isSingleTheme]);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', activeTheme);

        if (app && (isSingleTheme || (userTheme && userTheme !== lightThemeConfig && userTheme !== darkThemeConfig))) {
            localStorage.removeItem('theme');
        }
    }, [activeTheme, app, isSingleTheme, userTheme, lightThemeConfig, darkThemeConfig]);

    useEffect(() => {
        if (app?.name) {
            document.title = `${app.name} | inLoco.app`;
        }
    }, [app?.name]);

    const toggleTheme = useCallback(() => {
        if (isSingleTheme) return;

        const nextTheme = activeTheme === lightThemeConfig ? darkThemeConfig : lightThemeConfig;

        setUserTheme(nextTheme);
        localStorage.setItem('theme', nextTheme);
    }, [activeTheme, lightThemeConfig, darkThemeConfig, isSingleTheme]);

    const value = useMemo(() => ({
        theme: activeTheme,
        themes: AVAILABLE_THEMES as unknown as string[],
        darkTheme: darkThemeConfig,
        lightTheme: lightThemeConfig,
        toggleIsDisabled: isSingleTheme,
        toggleTheme
    }), [activeTheme, darkThemeConfig, lightThemeConfig, isSingleTheme, toggleTheme]);

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};